import { useState, useEffect, useRef } from 'react';
import { Outlet, Link, useLocation, useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../hooks/useTheme';
import { useAuth } from '../contexts/AuthContext';
import ApiErrorModal from '../components/ApiErrorModal';
import {
  GlobeIcon,
  SunIcon,
  MoonIcon,
  MonitorIcon,
  UserIcon,
  ShieldIcon,
  KeyIcon,
  TerminalIcon,
  CheckIcon,
  LogOutIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  PlusIcon,
  XIcon,
  LayoutDashboardIcon,
  UsersIcon,
  BuildingIcon
} from '../components/Icons';

export default function DashboardLayout() {
  const { theme, setThemeMode } = useTheme();
  const params = useParams();
  const navigate = useNavigate();

  const [isSidebarExpanded, setIsSidebarExpanded] = useState(() => {
    try {
      const raw = localStorage.getItem('sidebar_preference');
      return raw === null ? true : raw === 'true';
    } catch (_) {
      return true;
    }
  });

  const [notificationOpen, setNotificationOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [themeDropdownOpen, setThemeDropdownOpen] = useState(false);
  const profileButtonRef = useRef(null);
  const profileDropdownRef = useRef(null);
  const themeButtonRef = useRef(null);
  const themeDropdownRef = useRef(null);
  const notificationButtonRef = useRef(null);
  const notificationDropdownRef = useRef(null);
  const auth = useAuth();
  const [loggingOutCurrent, setLoggingOutCurrent] = useState(false);
  const [loggingOutAll, setLoggingOutAll] = useState(false);

  const currentUser = auth?.user || (auth?.getActiveUser ? auth.getActiveUser() : null);
  const isAdminUser = currentUser?.is_admin || currentUser?.is_super_admin;
  const [isAdminMode, setIsAdminMode] = useState(false);

  const [notifications] = useState([
    { id: 1, message: 'New login from Chrome on Windows', timestamp: '2 minutes ago', read: false },
    { id: 2, message: 'Profile updated successfully', timestamp: '1 hour ago', read: false },
    { id: 3, message: 'Security scan completed', timestamp: '3 hours ago', read: true },
  ]);
  const location = useLocation();
  const mainContentRef = useRef(null);
  const prevPathRef = useRef(location.pathname);

  // Reset scroll position to top whenever navigating between pages within the dashboard layout
  useEffect(() => {
    const prevPath = prevPathRef.current;
    prevPathRef.current = location.pathname;

    const normalizePath = (p) => (p || '').replace(/^\/u\/\d+/, '');

    // Prevent scrolling to top when switching users on the same page
    if (prevPath && normalizePath(prevPath) === normalizePath(location.pathname)) {
      return;
    }

    window.scrollTo(0, 0);
    if (document.documentElement) {
      document.documentElement.scrollTop = 0;
    }
    if (document.body) {
      document.body.scrollTop = 0;
    }
    if (mainContentRef.current) {
      mainContentRef.current.scrollTop = 0;
      mainContentRef.current.scrollLeft = 0;
    }
  }, [location.pathname]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const accountIndex = params?.accountIndex ?? null;
  const basePath = accountIndex !== null ? `/u/${accountIndex}` : '';

  useEffect(() => {
    function handleResize() {
      try {
        if (window.innerWidth < 1024) {
          setIsSidebarExpanded(false);
        } else {
          const raw = localStorage.getItem('sidebar_preference');
          const pref = raw === null ? true : raw === 'true';
          setIsSidebarExpanded(pref);
        }
      } catch (_) {}
    }

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const navLinks = isAdminMode
    ? [
        { label: 'System Approvals & Queue', path: `/admin/approvals`, icon: <CheckIcon className="w-5 h-5" /> },
        { label: 'User Governance', path: `/admin/users`, icon: <UsersIcon className="w-5 h-5" /> },
      ]
    : [
        { label: 'Overview & Security', path: `${basePath}/dashboard/overview`, icon: <LayoutDashboardIcon className="w-5 h-5" /> },
        { label: 'Profile Manager', path: `${basePath}/dashboard/profile`, icon: <UserIcon className="w-5 h-5" /> },
        { label: 'Developer Console', path: `${basePath}/dashboard/console`, icon: <TerminalIcon className="w-5 h-5" /> },
      ];

  useEffect(() => {
    const isAdminRoute = location.pathname.startsWith('/admin');
    if (isAdminRoute && !isAdminMode) {
      setIsAdminMode(true);
    } else if (!isAdminRoute && isAdminMode) {
      setIsAdminMode(false);
    }
  }, [location.pathname]);

  const clients = [
    'Hause Pro',
    'Property Pro',
    'Enterprise Solutions Inc',
    'Digital Gateway Corp',
  ];

  useEffect(() => {
    if (!auth?.loading && !auth?.isAuthenticated && !auth?.sessionExpired) {
      navigate('/', { replace: true });
    }
  }, [auth?.loading, auth?.isAuthenticated, auth?.sessionExpired, navigate]);

  useEffect(() => {
    if (auth?.activeIndex !== null && auth?.activeIndex !== undefined && params?.accountIndex !== undefined) {
      const currentParamIdx = parseInt(params.accountIndex, 10);
      if (!isNaN(currentParamIdx) && currentParamIdx !== auth.activeIndex) {
        const currentPath = location.pathname;
        const targetPath = currentPath.replace(/^\/u\/\d+/, `/u/${auth.activeIndex}`);
        navigate(targetPath, { replace: true });
      }
    }
  }, [auth?.activeIndex, params?.accountIndex, location.pathname, navigate]);

  useEffect(() => {
    if (auth?.sessionExpired || auth?.showLoginPopupModal) {
      window.open('/auth/popup', 'AuthPopup', 'width=500,height=650');
    }
  }, [auth?.sessionExpired, auth?.showLoginPopupModal]);


  // Handler for expired or broken avatar URLs: silently trigger /me to get a fresh pre-signed URL
  const handleAvatarError = (e) => {
    e.target.style.display = 'none';
    if (e.target.nextSibling) {
      e.target.nextSibling.style.display = 'flex';
    }
    if (auth?.refreshCurrentUser) {
      auth.refreshCurrentUser();
    }
  };

  useEffect(() => {
    function handleClickOutside(e) {
      if (profileDropdownOpen) {
        const btn = profileButtonRef.current;
        const menu = profileDropdownRef.current;
        if ((!btn || !btn.contains(e.target)) && (!menu || !menu.contains(e.target))) {
          setProfileDropdownOpen(false);
        }
      }

      if (themeDropdownOpen) {
        const btn = themeButtonRef.current;
        const menu = themeDropdownRef.current;
        if ((!btn || !btn.contains(e.target)) && (!menu || !menu.contains(e.target))) {
          setThemeDropdownOpen(false);
        }
      }

      if (notificationOpen) {
        const btn = notificationButtonRef.current;
        const menu = notificationDropdownRef.current;
        if ((!btn || !btn.contains(e.target)) && (!menu || !menu.contains(e.target))) {
          setNotificationOpen(false);
        }
      }
    }

    function handleEsc(e) {
      if (e.key === 'Escape') {
        setProfileDropdownOpen(false);
        setThemeDropdownOpen(false);
        setNotificationOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [profileDropdownOpen, themeDropdownOpen, notificationOpen]);

  const getInitials = (u) => {
    if (!u) return 'U';
    const name = (u.first_name ? `${u.first_name} ${u.last_name || ''}` : u.name || u.username || '').trim();
    const parts = name.split(' ').filter(Boolean);
    return (parts.length >= 2 ? parts[0][0] + parts[1][0] : (parts[0] ? parts[0][0] : 'U')).toUpperCase();
  };

  return (
    <div className="bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100 min-h-screen flex flex-col">
      {/* Fixed Sidebar */}
      <aside className={`fixed left-0 top-0 h-screen bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 transition-all duration-300 z-40 ${isSidebarExpanded ? 'w-64' : 'w-20'}`}>
        {/* Brand Logo Area - Clicking logo navigates to Home / */}
        <div className="p-5 border-b border-slate-200 dark:border-slate-800 flex items-center justify-center">
          <Link to="/" className="flex items-center gap-3 group cursor-pointer text-center">
            <div className="p-2 rounded-xl bg-indigo-600/10 text-indigo-600 dark:text-indigo-400 group-hover:scale-105 transition-transform">
              <GlobeIcon className="w-7 h-7" />
            </div>
            {isSidebarExpanded && (
              <span className="text-base font-bold tracking-tight text-slate-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                OneLogin SSO
              </span>
            )}
          </Link>
        </div>

        {/* Navigation Links */}
        <nav className="mt-6 px-3 space-y-1.5">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`flex items-center gap-3 px-3.5 py-2.5 rounded-xl transition-all font-medium text-sm ${
                location.pathname === link.path || location.pathname.startsWith(`${link.path}/`)
                  ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/20'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
              <span className="flex-shrink-0">{link.icon}</span>
              {isSidebarExpanded && <span className="truncate">{link.label}</span>}
            </Link>
          ))}
        </nav>

        {/* Active Indicator / Back Button */}
        <div className="absolute bottom-6 left-0 right-0 px-4">
          {isAdminMode ? (
            <button
              onClick={() => {
                setIsAdminMode(false);
                navigate('/u/0/dashboard/overview');
              }}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-rose-500/10 border border-rose-500/30 rounded-xl hover:bg-rose-500/20 transition-colors text-rose-600 dark:text-rose-400 text-xs font-semibold"
            >
              <span>← User Portal</span>
            </button>
          ) : (
            <div className="flex items-center justify-center gap-2.5 px-3 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
              <div className="h-2 w-2 bg-emerald-500 rounded-full animate-pulse" />
              {isSidebarExpanded && <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">System Connected</span>}
            </div>
          )}
        </div>

        {/* Sidebar Toggle */}
        <button
          onClick={() => {
            const next = !isSidebarExpanded;
            try {
              localStorage.setItem('sidebar_preference', String(next));
            } catch (_) {}
            setIsSidebarExpanded(next);
          }}
          className="absolute -right-3 top-7 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-sm rounded-full p-1 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors"
        >
          <ChevronRightIcon className={`w-3.5 h-3.5 transition-transform duration-200 ${isSidebarExpanded ? 'rotate-180' : ''}`} />
        </button>
      </aside>

      {/* Main Content */}
      <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarExpanded ? 'ml-64' : 'ml-20'}`}>
        {/* Top Navbar */}
        <header className="sticky top-0 z-30 bg-white/80 dark:bg-slate-900/80 backdrop-blur border-b border-slate-200 dark:border-slate-800 px-6 py-3.5">
          <div className="w-full flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h1 className="text-lg font-bold text-slate-900 dark:text-white">
                {isAdminMode ? 'Admin Governance' : 'Dashboard'}
              </h1>
            </div>

            <div className="flex items-center gap-3">
              {/* Theme Selection Dropdown */}
              <div className="relative">
                <button
                  ref={themeButtonRef}
                  onClick={() => setThemeDropdownOpen(!themeDropdownOpen)}
                  className="p-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                  aria-label="Theme selector"
                >
                  {theme === 'light' ? <SunIcon className="w-5 h-5 text-amber-500" /> : theme === 'dark' ? <MoonIcon className="w-5 h-5 text-indigo-400" /> : <MonitorIcon className="w-5 h-5 text-slate-400" />}
                </button>

                {themeDropdownOpen && (
                  <div ref={themeDropdownRef} className="absolute right-0 mt-2 w-44 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl z-50 overflow-hidden">
                    <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-700">
                      <p className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">Appearance</p>
                    </div>
                    <div className="p-1 space-y-0.5">
                      {[
                        { value: 'light', icon: <SunIcon className="w-4 h-4 text-amber-500" />, label: 'Light' },
                        { value: 'dark', icon: <MoonIcon className="w-4 h-4 text-indigo-400" />, label: 'Dark' },
                        { value: 'system', icon: <MonitorIcon className="w-4 h-4 text-slate-400" />, label: 'System' },
                      ].map((option) => (
                        <button
                          key={option.value}
                          onClick={() => {
                            setThemeMode(option.value);
                            setThemeDropdownOpen(false);
                          }}
                          className={`w-full text-left px-3 py-2 rounded-lg flex items-center justify-between text-xs transition-colors ${
                            theme === option.value
                              ? 'bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400 font-semibold'
                              : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50'
                          }`}
                        >
                          <div className="flex items-center gap-2.5">
                            {option.icon}
                            <span>{option.label}</span>
                          </div>
                          {theme === option.value && <CheckIcon className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Notification Bell */}
              <div className="relative">
                <button
                  ref={notificationButtonRef}
                  onClick={() => setNotificationOpen(!notificationOpen)}
                  className="p-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors relative"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 bg-indigo-600 text-white text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center ring-2 ring-white dark:ring-slate-900">
                      {unreadCount}
                    </span>
                  )}
                </button>

                {notificationOpen && (
                  <div ref={notificationDropdownRef} className="absolute right-0 mt-2 w-80 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl z-50 overflow-hidden">
                    <div className="p-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                      <h3 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">Notifications</h3>
                    </div>
                    <div className="max-h-80 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-700/50">
                      {notifications.map((notif) => (
                        <div key={notif.id} className="p-3 hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors cursor-pointer">
                          <p className="text-xs font-medium text-slate-800 dark:text-slate-200">{notif.message}</p>
                          <p className="text-[11px] text-slate-400 mt-1">{notif.timestamp}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Profile Dropdown Component */}
              <div className="relative">
                <button
                  ref={profileButtonRef}
                  onClick={() => setProfileDropdownOpen((s) => !s)}
                  className="flex items-center gap-2.5 p-1.5 pr-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
                >
                  <div className="h-8 w-8 rounded-lg bg-indigo-600 flex items-center justify-center text-white text-xs font-bold shadow-sm overflow-hidden">
                    {(currentUser?.profile_image_url) ? (
                      <img
                        src={currentUser.profile_image_url}
                        alt="Profile"
                        className="h-full w-full object-cover"
                        onError={handleAvatarError}
                      />
                    ) : null}
                    <span style={{ display: currentUser?.profile_image_url ? 'none' : 'flex' }} className="h-full w-full items-center justify-center">
                      {getInitials(currentUser)}
                    </span>
                  </div>
                  <span className="text-xs font-semibold text-slate-800 dark:text-slate-200 max-w-[100px] truncate">
                    {currentUser?.first_name || currentUser?.name || currentUser?.username || 'Account'}
                  </span>
                  <ChevronDownIcon className="w-4 h-4 text-slate-400" />
                </button>

                {profileDropdownOpen && (
                  <div ref={profileDropdownRef} className="absolute right-0 mt-2 w-80 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-2xl z-50 overflow-hidden divide-y divide-slate-100 dark:divide-slate-700/60">
                    {/* User header card */}
                    <div className="p-4 bg-slate-50/60 dark:bg-slate-900/50 flex items-center gap-3">
                      <div className="h-11 w-11 rounded-xl bg-indigo-600 flex items-center justify-center text-white text-sm font-bold shadow-md shadow-indigo-600/20 overflow-hidden">
                        {(currentUser?.profile_image_url) ? (
                          <img
                            src={currentUser.profile_image_url}
                            alt="Profile"
                            className="h-full w-full object-cover"
                            onError={handleAvatarError}
                          />
                        ) : null}
                        <span style={{ display: currentUser?.profile_image_url ? 'none' : 'flex' }} className="h-full w-full items-center justify-center">
                          {getInitials(currentUser)}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="text-xs font-bold text-slate-900 dark:text-white truncate">
                            {currentUser?.first_name ? `${currentUser.first_name} ${currentUser.last_name || ''}` : currentUser?.name || currentUser?.username || 'User'}
                          </h4>
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-800">
                            Active
                          </span>
                        </div>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate mt-0.5">
                          {currentUser?.email || currentUser?.username || 'SSO User'}
                        </p>
                      </div>
                    </div>

                    {/* Accounts Switcher Section */}
                    {auth?.getAccountsList && auth.getAccountsList().length > 0 && (
                      <div className="p-3">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2 px-1">Switch Account</p>
                        <div className="space-y-1">
                          {auth.getAccountsList().map((acc) => {
                            const isCurrent = acc.active;
                            const u = acc.user;
                            const displayName = u
                              ? (u.first_name ? `${u.first_name} ${u.last_name || ''}`.trim() : (u.username || u.email || `Account ${acc.index + 1}`))
                              : `Account ${acc.index + 1}`;
                            return (
                              <button
                                key={acc.index}
                                onClick={() => {
                                  setProfileDropdownOpen(false);
                                  const currentScrollY = window.scrollY || document.documentElement?.scrollTop || 0;
                                  const currentContainerScroll = mainContentRef.current ? mainContentRef.current.scrollTop : 0;

                                  auth.switchAccount(acc.index);
                                  const currentPath = location.pathname;
                                  const targetPath = currentPath.replace(/^\/u\/\d+/, `/u/${acc.index}`);
                                  navigate(targetPath);

                                  requestAnimationFrame(() => {
                                    window.scrollTo(0, currentScrollY);
                                    if (mainContentRef.current) {
                                      mainContentRef.current.scrollTop = currentContainerScroll;
                                    }
                                  });
                                }}
                                className={`w-full text-left px-3 py-2 rounded-xl flex items-center justify-between text-xs transition-colors ${
                                  isCurrent
                                    ? 'bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400 font-semibold border border-indigo-200 dark:border-indigo-800/60'
                                    : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50'
                                }`}
                              >
                                <div className="flex items-center gap-2 truncate">
                                  <UserIcon className="w-4 h-4 text-slate-400 shrink-0" />
                                  <div className="flex flex-col min-w-0">
                                    <span className="truncate font-medium">{displayName}</span>
                                    {u?.email && u.email !== displayName && (
                                      <span className="text-[10px] text-slate-400 dark:text-slate-500 truncate font-normal">
                                        {u.email}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                {isCurrent && (
                                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900 text-indigo-600 dark:text-indigo-300 shrink-0">
                                    Default
                                  </span>
                                )}
                              </button>
                            );
                          })}

                          <button
                            onClick={() => {
                              setProfileDropdownOpen(false);
                              window.open('/auth/popup', 'AuthPopup', 'width=500,height=650');
                            }}
                            className="w-full text-left px-3 py-2 mt-1 rounded-xl flex items-center gap-2 text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 transition-colors border border-dashed border-indigo-200 dark:border-indigo-800"
                          >
                            <PlusIcon className="w-4 h-4" />
                            <span>Add another account</span>
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Quick Settings & Navigation */}
                    <div className="p-2 space-y-0.5">
                      <button
                        onClick={() => {
                          setProfileDropdownOpen(false);
                          if (isAdminMode) {
                            setIsAdminMode(false);
                          }
                          navigate(`/u/${auth?.activeIndex ?? 0}/dashboard/profile`);
                        }}
                        className="w-full text-left px-3 py-2 rounded-xl flex items-center gap-2.5 text-xs font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors"
                      >
                        <UserIcon className="w-4 h-4 text-slate-400" />
                        <span>Profile Settings</span>
                      </button>

                      {isAdminUser && (
                        <button
                          onClick={() => {
                            setProfileDropdownOpen(false);
                            if (isAdminMode) {
                              setIsAdminMode(false);
                              navigate('/u/0/dashboard/overview');
                            } else {
                              setIsAdminMode(true);
                              navigate('/admin/approvals');
                            }
                          }}
                          className="w-full text-left px-3 py-2 rounded-xl flex items-center gap-2.5 text-xs font-medium text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/30 transition-colors"
                        >
                          <ShieldIcon className="w-4 h-4 text-amber-500" />
                          <span>{isAdminMode ? 'Switch to User View' : 'Switch to Admin View'}</span>
                        </button>
                      )}
                    </div>

                    {/* Logout Actions */}
                    <div className="p-2 bg-slate-50/50 dark:bg-slate-900/30 space-y-1">
                      <button
                        type="button"
                        disabled={auth?.isLoggingOut || loggingOutCurrent || loggingOutAll}
                        onClick={async () => {
                          setLoggingOutCurrent(true);
                          try {
                            const res = await auth.logout(auth.activeIndex);
                            setProfileDropdownOpen(false);
                            if (!res || res.remainingCount === 0) {
                              navigate('/');
                            } else {
                              navigate(`/u/${res.newActiveIndex}/dashboard/overview`);
                            }
                          } catch (err) {
                            console.error('Logout error', err);
                            setProfileDropdownOpen(false);
                            navigate('/');
                          } finally {
                            setLoggingOutCurrent(false);
                          }
                        }}
                        className="w-full text-left px-3 py-2 rounded-xl flex items-center gap-2.5 text-xs font-semibold text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/30 transition-colors disabled:opacity-50"
                      >
                        {loggingOutCurrent ? (
                          <div className="w-4 h-4 border-2 border-rose-600 dark:border-rose-400 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                        ) : (
                          <LogOutIcon className="w-4 h-4 flex-shrink-0" />
                        )}
                        <span>{loggingOutCurrent ? 'Logging out...' : 'Log Out Current Account'}</span>
                      </button>

                      <button
                        type="button"
                        disabled={auth?.isLoggingOut || loggingOutCurrent || loggingOutAll}
                        onClick={async () => {
                          setLoggingOutAll(true);
                          try {
                            await auth.logoutAll();
                          } catch (err) {
                            console.error('Logout all error', err);
                          } finally {
                            setLoggingOutAll(false);
                            setProfileDropdownOpen(false);
                            navigate('/');
                          }
                        }}
                        className="w-full text-left px-3 py-2 rounded-xl flex items-center gap-2.5 text-xs font-semibold text-rose-700 dark:text-rose-500 hover:bg-rose-100 dark:hover:bg-rose-950/60 transition-colors disabled:opacity-50"
                      >
                        {loggingOutAll ? (
                          <div className="w-4 h-4 border-2 border-rose-700 dark:border-rose-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                        ) : (
                          <XIcon className="w-4 h-4 flex-shrink-0" />
                        )}
                        <span>{loggingOutAll ? 'Logging out all accounts...' : 'Log Out All Accounts'}</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page Content Container */}
        <main ref={mainContentRef} data-scroll-container className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 p-8 mt-12">
          <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white mb-4">Enterprise Clients</h3>
              <ul className="space-y-2">
                {clients.map((client) => (
                  <li key={client} className="text-xs text-slate-600 dark:text-slate-400 flex items-center gap-2">
                    <CheckIcon className="w-3.5 h-3.5 text-emerald-500" />
                    <span>{client}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white mb-4">Product</h3>
              <ul className="space-y-2">
                <li className="text-xs text-slate-600 dark:text-slate-400 hover:text-indigo-600 cursor-pointer">Features</li>
                <li>
                  <Link to="/about" className="text-xs text-slate-600 dark:text-slate-400 hover:text-indigo-600">About Us</Link>
                </li>
                <li className="text-xs text-slate-600 dark:text-slate-400 hover:text-indigo-600 cursor-pointer">Security</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white mb-4">Support</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/help" className="text-xs text-slate-600 dark:text-slate-400 hover:text-indigo-600">Help Center</Link>
                </li>
                <li className="text-xs text-slate-600 dark:text-slate-400 hover:text-indigo-600 cursor-pointer">Documentation</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white mb-4">Legal</h3>
              <ul className="space-y-2">
                <li className="text-xs text-slate-600 dark:text-slate-400 hover:text-indigo-600 cursor-pointer">Privacy Policy</li>
                <li>
                  <Link to="/contact" className="text-xs text-slate-600 dark:text-slate-400 hover:text-indigo-600">Support Contact</Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="max-w-7xl mx-auto mt-8 pt-6 border-t border-slate-200 dark:border-slate-800 text-center text-xs text-slate-500">
            <p>&copy; 2026 OneLogin SSO Platform. All rights reserved.</p>
          </div>
        </footer>
      </div>

      <ApiErrorModal
        isOpen={!!auth?.authError}
        title={auth?.authError?.title || 'Service Error'}
        message={auth?.authError?.message || 'An error occurred while communicating with the server.'}
        details={auth?.authError?.details}
        onTryAgain={auth?.authError?.retryFn}
        onReload={() => window.location.reload()}
        onCancel={() => auth?.clearAuthError?.()}
      />

      {/* Mid-Work In-Place Session Expired Recovery Modal */}
      {auth?.sessionExpired && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="w-full max-w-md bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-2xl border border-slate-200 dark:border-slate-700 text-center animate-in fade-in zoom-in-95 duration-150">
            <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 mx-auto flex items-center justify-center mb-4">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">Session Expired</h3>
            <p className="text-sm text-slate-600 dark:text-slate-300 mb-6 leading-relaxed">
              Your authentication session has expired while working. Complete sign-in in the popup window to resume without losing your in-progress work.
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={() => auth?.logout?.()}
                className="px-4 py-2.5 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors rounded-xl"
              >
                Log Out
              </button>
              <button
                type="button"
                onClick={() => window.open('/auth/popup', 'AuthPopup', 'width=500,height=650')}
                className="px-5 py-2.5 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl shadow-md shadow-indigo-600/20 transition-colors"
              >
                Sign In to Resume
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
